import axios from "axios";

// Arquivo grande (ZIP ate 1 GB) vai para a API em partes de ate 32 MB: a API
// repassa cada parte ao storage e, no fim, junta o arquivo. O navegador nao fala
// com o storage, e cada requisicao fica abaixo do limite do proxy (Cloudflare).
// Devolve { key, name, size, content_type }: a chave e o que o registro do
// documento recebe (uploaded_key).
export const CHUNKED_UPLOAD_MAX_BYTES = 1024 * 1024 * 1024;
export const CHUNKED_UPLOAD_MIN_BYTES = 8 * 1024 * 1024;
export const ACCEPTED_DOCUMENT_TYPES = ".pdf,.png,.jpg,.jpeg,.webp,.csv,.xls,.xlsx,.zip,application/pdf,image/png,image/jpeg,application/zip,application/x-zip-compressed";

const BASE_PATH = "/api/uploads";
// Tentativas por parte em falha de rede ou do servidor (5xx).
const PART_ATTEMPTS = 3;

export function isZipFile(file) {
  return !!file && /\.zip$/i.test(file.name || "");
}

// ZIP sempre vai em partes; os demais so quando passam do limite do caminho normal da API.
export function needsChunkedUpload(file) {
  if (!file) return false;
  return isZipFile(file) || Number(file.size || 0) > CHUNKED_UPLOAD_MIN_BYTES;
}

export function validateDocumentFile(file) {
  if (!file) return "Selecione um arquivo.";
  if (Number(file.size || 0) > CHUNKED_UPLOAD_MAX_BYTES) return "O arquivo excede o limite de 1 GB.";
  return null;
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function canRetry(error) {
  if (axios.isCancel(error)) return false;
  const status = error && error.response && error.response.status;
  return !status || status >= 500;
}

async function sendPart(url, chunk, { signal, onBytes }) {
  for (let attempt = 1; ; attempt++) {
    try {
      return await axios.put(url, chunk, {
        headers: { "Content-Type": "application/octet-stream" },
        timeout: 0,
        signal,
        onUploadProgress: (event) => onBytes(Math.min(event.loaded || 0, chunk.size)),
      });
    } catch (error) {
      if (attempt >= PART_ATTEMPTS || !canRetry(error)) throw error;
      onBytes(0);
      await wait(1000 * attempt);
    }
  }
}

// options.onProgress(percent, { loaded, total }); options.signal (AbortController) cancela.
export async function chunkedUpload(file, options = {}) {
  const basePath = options.basePath || BASE_PATH;
  const onProgress = typeof options.onProgress === "function" ? options.onProgress : () => {};
  const contentType = file.type || (isZipFile(file) ? "application/zip" : "application/octet-stream");
  const total = file.size;

  const started = await axios.post(basePath, { file_name: file.name, size: total, content_type: contentType }, { signal: options.signal });
  const session = started.data.content || started.data;
  const url = `${basePath}/${session.id}`;
  let done = 0;
  // 100% so depois da conclusao: a API ainda junta as partes no storage.
  const report = (inFlight) => {
    const loaded = Math.min(total, done + inFlight);
    onProgress(Math.min(99, Math.floor((loaded / total) * 100)), { loaded, total });
  };

  try {
    for (let number = 1; number <= session.parts; number++) {
      const begin = (number - 1) * session.part_size;
      const chunk = file.slice(begin, Math.min(begin + session.part_size, total));
      await sendPart(`${url}/parts/${number}`, chunk, { signal: options.signal, onBytes: report });
      done += chunk.size;
      report(0);
    }
    const completed = await axios.post(`${url}/complete`, null, { signal: options.signal, timeout: 0 });
    onProgress(100, { loaded: total, total });
    return completed.data.content || completed.data;
  } catch (error) {
    // Descarta no storage as partes ja enviadas (melhor esforco).
    axios.delete(url).catch(() => {});
    throw error;
  }
}
